import React, { useEffect, useMemo } from 'react';
import ParameterGroup from './ParameterGroup';

const ParameterAlgorithmTabs = ({ rootGroup, algorithmGroups, values, onChange, disabled, constraints = {} }) => {
  const safeAlgorithmGroups = useMemo(
    () => (Array.isArray(algorithmGroups) ? algorithmGroups : []),
    [algorithmGroups]
  );

  const parentId = useMemo(() => {
    if (rootGroup?.id) {
      return rootGroup.id;
    }
    return safeAlgorithmGroups[0]?.rootId;
  }, [rootGroup, safeAlgorithmGroups]);
  const algorithmControlId = `${parentId}.algorithm`;

  const options = useMemo(() => {
    if (rootGroup?.meta?.algorithm?.options?.length) {
      return rootGroup.meta.algorithm.options;
    }
    return safeAlgorithmGroups.map((group) => group.meta?.algorithmId).filter(Boolean);
  }, [rootGroup, safeAlgorithmGroups]);

  const defaultAlgorithm = useMemo(() => {
    if (rootGroup?.meta?.algorithm?.default) {
      return rootGroup.meta.algorithm.default;
    }
    const firstAvailable = safeAlgorithmGroups.find(
      (group) => group.meta?.available !== false || group.meta?.show_toggle === true
    );
    return firstAvailable?.meta?.algorithmId || options[0] || null;
  }, [rootGroup, safeAlgorithmGroups, options]);

  const selectedAlgorithm = values?.[algorithmControlId] ?? defaultAlgorithm;

  useEffect(() => {
    if (!selectedAlgorithm && defaultAlgorithm) {
      const control = {
        id: algorithmControlId,
        name: 'algorithm',
        label: 'Algorithm',
        controlType: 'select',
      };
      onChange(control, defaultAlgorithm);
    }
  }, [selectedAlgorithm, defaultAlgorithm, algorithmControlId, onChange]);

  const handleSelect = (algorithmId) => {
    if (disabled) return;
    const control = {
      id: algorithmControlId,
      name: 'algorithm',
      label: 'Algorithm',
      controlType: 'select',
    };
    onChange(control, algorithmId);
  };

  const activeGroup = safeAlgorithmGroups.find(
    (group) => group.meta?.algorithmId === selectedAlgorithm
  ) || safeAlgorithmGroups[0];

  const labelForGroup = (group) => {
    if (group.meta?.label) {
      return group.meta.label;
    }
    if (group.meta?.algorithmId && rootGroup?.meta?.algorithm?.optionLabels) {
      const mapped = rootGroup.meta.algorithm.optionLabels[group.meta.algorithmId];
      if (mapped) {
        return mapped;
      }
    }
    if (group.title?.includes('›')) {
      return group.title.split('›').pop().trim();
    }
    return group.title || group.meta?.algorithmId || 'Algorithm';
  };

  const renderTab = (group) => {
    const algorithmId = group.meta?.algorithmId;
    if (!algorithmId) {
      return null;
    }
    const unavailable = group.meta?.available === false && group.meta?.show_toggle !== true;
    const locked = group.meta?.locked === true;
    const isActive = algorithmId === selectedAlgorithm;
    return (
      <button
        key={algorithmId}
        type="button"
        className={`parameter-algorithm__tab${isActive ? ' parameter-algorithm__tab--active' : ''}${
          unavailable || locked ? ' parameter-algorithm__tab--disabled' : ''
        }`}
        onClick={() => handleSelect(algorithmId)}
        disabled={disabled || unavailable || locked}
        aria-pressed={isActive}
      >
        <span>{labelForGroup(group)}</span>
        {(unavailable || locked) && <small>Locked</small>}
      </button>
    );
  };

  return (
    <div className="parameter-algorithm">
      <div className="parameter-algorithm__tabs">
        {safeAlgorithmGroups.map((group) => renderTab(group))}
      </div>
      {activeGroup ? (
        <div className="parameter-algorithm__panel">
          <ParameterGroup
            key={activeGroup.id}
            group={activeGroup}
            values={values}
            onChange={onChange}
            disabled={disabled || activeGroup.meta?.available === false}
            constraints={constraints}
            hideHeader
          />
        </div>
      ) : null}
    </div>
  );
};

export default ParameterAlgorithmTabs;
